// Preuve de paiement « en attente » — Cache Storage navigateur.
//
// Contexte : la confirmation d'e-mail étant active, `signUp` ne renvoie AUCUNE
// session. La preuve ne peut donc pas être téléversée dans le bucket privé
// `preuves-abonnements` au moment du clic « Inscription » (un upload anonyme
// serait refusé par RLS, et l'autoriser serait une faille).
//
// Solution : la preuve choisie dans le formulaire est conservée localement
// (Cache Storage, données temporaires, jamais exposées publiquement), puis elle
// est envoyée automatiquement à la PREMIÈRE CONNEXION — le client ne la
// re-fournit jamais.
//
// Aucune donnée fictive. Aucun secret. Le bucket de destination reste privé.

const CACHE_NAME = "immo-preuves-en-attente";

function emailToKey(email: string): string {
  return `/__preuves-en-attente__/${encodeURIComponent(email.trim().toLowerCase())}`;
}

/** Met en cache la preuve du nouvel inscrit (best effort : ne lève jamais). */
export async function stashPendingPreuve(email: string, file: File): Promise<void> {
  if (!email || typeof caches === "undefined") return;
  try {
    const cache = await caches.open(CACHE_NAME);
    const response = new Response(file, {
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        "X-Pending-Filename": encodeURIComponent(file.name || "preuve"),
      },
    });
    await cache.put(emailToKey(email), response);
  } catch {
    /* non bloquant : au pire la preuve sera redemandée à la première connexion */
  }
}

/** Récupère la preuve mise en cache, ou null si absente. */
export async function readPendingPreuve(email: string): Promise<File | null> {
  if (!email || typeof caches === "undefined") return null;
  try {
    const cache = await caches.open(CACHE_NAME);
    const response = await cache.match(emailToKey(email));
    if (!response) return null;
    const blob = await response.blob();
    const name = decodeURIComponent(response.headers.get("X-Pending-Filename") || "preuve");
    const type = blob.type || response.headers.get("Content-Type") || "application/octet-stream";
    return new File([blob], name, { type });
  } catch {
    return null;
  }
}

/** Supprime la preuve mise en cache (après transmission réussie). */
export async function clearPendingPreuve(email: string): Promise<void> {
  if (!email || typeof caches === "undefined") return;
  try {
    const cache = await caches.open(CACHE_NAME);
    await cache.delete(emailToKey(email));
  } catch {
    /* ignore */
  }
}